/**
 * Secure token storage — the native (Keystore-backed) `TokenProvider` the
 * HttpClient consumes inside the Android WebView (T27.2.2.2).
 *
 * Why this exists. On web the access token lives in `localStorage` and the
 * refresh token is an httpOnly `sameSite:'lax'` cookie (see
 * `auth/tokenStorage.ts`). In the native WebView the origin is `https://android.shopista.ca`
 * calling a different API origin, so that cookie is cross-site and never reaches
 * `/refresh` — the session is forced to re-login on access-token expiry. The fix
 * is to store BOTH tokens in the Android Keystore and send the refresh token
 * explicitly on `/refresh` (the client adds the `X-Refresh-Token` header when
 * `getRefreshToken` returns a value).
 *
 * Sync surface over an async store. `TokenProvider` is synchronous, but the
 * Keystore plugin's reads/writes are async. We bridge with an in-memory mirror:
 *   - `hydrate()` (called once at app bootstrap, before the first request)
 *     loads the persisted tokens into the mirror.
 *   - `getAccessToken` / `getRefreshToken` read the mirror synchronously.
 *   - `setAccessToken` / `setRefreshToken` / `clearTokens` update the mirror
 *     synchronously AND enqueue a durable write to the Keystore.
 *
 * Durability (the on-device defect this module hardens against, T27.2.2.2 fix).
 * The real failure was in the Keystore plugin's Android persistence, not here.
 * `@aparajita/capacitor-secure-storage` wrote its ciphertext via
 * `SharedPreferences.edit().putString(...).apply()`. `.apply()` schedules the
 * disk write ASYNCHRONOUSLY and the plugin's `setItem()` resolves BEFORE the
 * bytes land on disk. So when Android killed a backgrounded, logged-in process
 * (lock + idle → lowmemorykiller) before that background flush completed, the
 * token never reached disk → the next cold-start `hydrate()` read `null` →
 * forced re-login. Intermittent race. (The `KEY_NOT_FOUND` we also saw is
 * benign: it's the plugin generating the AES key on the first write of a key.)
 *
 * The fix is a vendored patch (`patches/@aparajita__capacitor-secure-storage.patch`)
 * that swaps the persisting `.apply()` calls for `.commit()` — synchronous,
 * returning only after the disk write. With that patch the plugin's `setItem()`
 * resolves only once the token is durably on disk.
 *
 * What this module contributes on top of the patched plugin:
 *   - Writes are SERIALIZED through a single-flight queue, so login and a
 *     concurrent refresh-rotation can't interleave their writes.
 *   - `flush()` awaits the pending write chain. `main.tsx` calls it on the
 *     Capacitor `App` background lifecycle. Because `setItem()` now resolves
 *     after the synchronous `.commit()`, awaiting `flush()` genuinely means the
 *     token is on disk before the OS can reclaim the process.
 *   - Failures surface through an injectable `onWriteError` / `onReadError`
 *     diagnostic (the key NAME only — NEVER the token value, which must never
 *     reach logcat). The in-memory mirror keeps the live session alive
 *     regardless, so a write failure never breaks the request pipeline.
 *
 * Note: there is deliberately NO verify-after-write read-back here. A read-back
 * would hit the SAME in-memory SharedPreferences map the write just populated,
 * so it always "passes" and proves nothing about disk durability — that gate is
 * the plugin's `.commit()`, and the only real proof is an on-device disk check.
 *
 * Portability (CP1–CP4). This file lives in `apps/web` precisely because the
 * `@capacitor/*` plugin import is allowed here, never in `packages/shared`. The
 * plugin is reached only on a native platform and dynamically imported, so the
 * web bundle never eagerly loads it. The shared HttpClient only ever sees the
 * injected `TokenProvider` — nothing Capacitor-shaped leaks across the seam.
 */

import type { WebTokenStorage } from '../auth/tokenStorage';

/** Keystore keys — kept distinct from the web `localStorage` keys on purpose. */
export const SECURE_ACCESS_TOKEN_KEY = 'shopista.accessToken';
export const SECURE_REFRESH_TOKEN_KEY = 'shopista.refreshToken';
export const SECURE_CACHED_USER_KEY = 'shopista.user';

/**
 * Minimal async secure-store seam. The native impl wraps the Keystore plugin;
 * tests inject a fake. Keeping this an interface (not the plugin directly) is
 * what makes the adapter unit-testable without a device.
 */
export interface SecureStore {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

/**
 * Optional diagnostics for the secure store. Both receive the affected key NAME
 * and a redacted error — NEVER the token value (secrets must not hit logcat).
 * Default no-op; `main.tsx` wires a console diagnostic on native.
 */
export interface SecureTokenStorageDiagnostics {
  onWriteError?: (key: string, error: unknown) => void;
  onReadError?: (key: string, error: unknown) => void;
}

/**
 * The native token provider. Satisfies `WebTokenStorage` so it slots into the
 * existing web auth layer (`TokensProvider`, `session.ts`, `OAuthCallback`)
 * unchanged — the only difference is where the bytes live (Keystore, not
 * `localStorage`) and that the refresh accessors are populated. Adds the async
 * `hydrate` bootstrap hook the Keystore needs to fill the synchronous mirror,
 * and `flush` so a backgrounded process can durably commit before being killed.
 */
export interface SecureTokenStorage extends WebTokenStorage {
  /**
   * Load persisted tokens + cached user from the secure store into the
   * in-memory mirror. Call once at app bootstrap, before the HttpClient's first
   * request, so `getAccessToken()` / `getCachedUser()` return cold-start values
   * synchronously.
   */
  hydrate(): Promise<void>;
  /**
   * Await every enqueued write so far. Call on the native `App` `pause`
   * lifecycle so a backgrounded process durably commits its tokens to disk
   * BEFORE the OS can reclaim it (the kill-race the durability fix closes).
   * Resolves even if some writes failed (failures surface via `onWriteError`).
   */
  flush(): Promise<void>;
  // Refresh accessors are REQUIRED on the native provider (the whole point of
  // this adapter), narrowing the optional shape from `TokenProvider`.
  getRefreshToken(): string | null;
  // Returns the durable-write promise so the refresh path can await the rotated
  // token onto disk before resolving (T27.2.2.2) — compatible with the shared
  // `TokenProvider.setRefreshToken?(): void | Promise<void>` seam.
  setRefreshToken(token: string | null): Promise<void>;
}

export interface MakeSecureTokenStorageOptions {
  /** The async secure store. Defaults to the Keystore-backed native impl. */
  store?: SecureStore;
  /** Optional redacted diagnostics (key names only, never values). */
  diagnostics?: SecureTokenStorageDiagnostics;
}

/**
 * Build the native secure `TokenProvider`. `store` defaults to the Keystore
 * impl; tests inject a fake `SecureStore`.
 */
export function makeSecureTokenStorage(
  options: MakeSecureTokenStorageOptions = {}
): SecureTokenStorage {
  const store = options.store ?? createKeystoreSecureStore();
  const diag = options.diagnostics ?? {};

  // In-memory mirror — the synchronous source of truth the HttpClient reads.
  let access: string | null = null;
  let refresh: string | null = null;
  let cachedUserRaw: string | null = null;

  // Single-flight write queue. Every mirror mutation appends its durable write
  // to this chain so concurrent writers (login + a refresh-rotation) serialize
  // their two-store (Keystore + SharedPreferences) operations instead of
  // interleaving. `flush()` awaits the tail; the chain never rejects (failures
  // are caught + reported), so one bad write can't wedge the queue.
  let writeChain: Promise<void> = Promise.resolve();

  function enqueue(task: () => Promise<void>): void {
    writeChain = writeChain.then(task, task);
  }

  // Durable write of a single key: persist the value, or remove on null. There
  // is intentionally no read-back verification — disk durability is guaranteed
  // by the plugin's `.commit()` patch, not by reading the in-memory mirror back.
  async function durableWrite(key: string, value: string | null): Promise<void> {
    try {
      if (value === null) {
        await store.remove(key);
      } else {
        await store.set(key, value);
      }
    } catch (error) {
      // The in-memory mirror still holds the live value for this session, so a
      // persistence failure never breaks the request pipeline — but it MUST be
      // visible (key name only, no value), not silently swallowed.
      diag.onWriteError?.(key, error);
    }
  }

  function writeThrough(key: string, value: string | null): void {
    enqueue(() => durableWrite(key, value));
  }

  /**
   * Like `writeThrough`, but returns the tail of the write chain so the caller
   * can `await` durability (T27.2.2.2). Because the chain is FIFO, awaiting this
   * tail also drains every write enqueued before it — so the refresh path awaits
   * `setRefreshToken` LAST and gets the queued access-token write committed for
   * free. The chain never rejects (failures are caught + reported), so the await
   * always resolves.
   */
  function writeThroughAwaitable(key: string, value: string | null): Promise<void> {
    enqueue(() => durableWrite(key, value));
    return writeChain;
  }

  return {
    async hydrate(): Promise<void> {
      // Read each key independently so one unreadable entry (e.g. a corrupt or
      // desynced alias) doesn't blank the others. A read failure leaves that
      // field null and is reported; the user re-logs in only if BOTH tokens are
      // unrecoverable.
      access = await safeRead(store, SECURE_ACCESS_TOKEN_KEY, diag);
      refresh = await safeRead(store, SECURE_REFRESH_TOKEN_KEY, diag);
      cachedUserRaw = await safeRead(store, SECURE_CACHED_USER_KEY, diag);
    },

    async flush(): Promise<void> {
      // Await the current tail of the write chain. New writes enqueued after
      // this call are not awaited (they belong to a later flush), which is the
      // correct semantics for a lifecycle `pause` checkpoint.
      await writeChain;
    },

    getAccessToken(): string | null {
      return access;
    },

    setAccessToken(token: string | null): void {
      access = token;
      writeThrough(SECURE_ACCESS_TOKEN_KEY, token);
    },

    getRefreshToken(): string | null {
      return refresh;
    },

    setRefreshToken(token: string | null): Promise<void> {
      refresh = token;
      // Return the durable-write promise so the refresh path can await the
      // rotated token onto disk before it resolves (T27.2.2.2). Awaiting this
      // tail also drains the access-token write queued just before it.
      return writeThroughAwaitable(SECURE_REFRESH_TOKEN_KEY, token);
    },

    clearTokens(): void {
      access = null;
      refresh = null;
      cachedUserRaw = null;
      writeThrough(SECURE_ACCESS_TOKEN_KEY, null);
      writeThrough(SECURE_REFRESH_TOKEN_KEY, null);
      writeThrough(SECURE_CACHED_USER_KEY, null);
    },

    setSession({ accessToken, user }): void {
      access = accessToken;
      writeThrough(SECURE_ACCESS_TOKEN_KEY, accessToken);
      try {
        cachedUserRaw = JSON.stringify(user);
        writeThrough(SECURE_CACHED_USER_KEY, cachedUserRaw);
      } catch {
        // user wasn't serializable — the access token alone is sufficient for
        // bootstrap (/auth/me re-fetches the canonical user).
      }
    },

    getCachedUser(): unknown | null {
      if (!cachedUserRaw) return null;
      try {
        return JSON.parse(cachedUserRaw);
      } catch {
        cachedUserRaw = null;
        writeThrough(SECURE_CACHED_USER_KEY, null);
        return null;
      }
    },
  };
}

/** Read one key, tolerating a store read error by reporting it + returning null. */
async function safeRead(
  store: SecureStore,
  key: string,
  diag: SecureTokenStorageDiagnostics
): Promise<string | null> {
  try {
    return await store.get(key);
  } catch (error) {
    diag.onReadError?.(key, error);
    return null;
  }
}

/**
 * Keystore-backed `SecureStore` (native only). Dynamically imports the
 * Capacitor secure-storage plugin so the web bundle never loads it. Reached
 * only after `isNativePlatform()` is true at the call site in `api.ts`.
 *
 * Plugin: `@aparajita/capacitor-secure-storage` — Cap-7-compatible, backed by
 * the Android Keystore (AES-GCM key in the Keystore + ciphertext in
 * `SharedPreferences`). The v7 API is the `SecureStorage` singleton exposing
 * `getItem` / `setItem` / `removeItem`. `getItem` returns the stored value
 * (string for our usage) or `null` for a missing key — it does NOT throw on a
 * missing key (it rejects only on a real Keystore failure), so the absent-key
 * sentinel is `null`.
 */
export function createKeystoreSecureStore(): SecureStore {
  return {
    async get(key: string): Promise<string | null> {
      const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');
      const value = await SecureStorage.getItem(key);
      return typeof value === 'string' ? value : null;
    },
    async set(key: string, value: string): Promise<void> {
      const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');
      await SecureStorage.setItem(key, value);
    },
    async remove(key: string): Promise<void> {
      const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');
      await SecureStorage.removeItem(key);
    },
  };
}
